import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useUser } from '../../config/UserContext';
import styles from './EmojiArray.module.scss';
import ReactionsModal from './ReactionsModal';
import logger from '../../utils/logger';

const MAX_VISIBLE_EMOJIS = 3;

const EmojiArray = ({ emojiReactions }) => {
  const [reactionUsers, setReactionUsers] = useState({});
  const { getUserDetailsById } = useUser();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const hasReactions = useMemo(
    () => emojiReactions?.length > 0,
    [emojiReactions]
  );

  // The most recent distinct emojis, newest first. Reactions are appended as
  // they happen, so the end of the array is the newest.
  const topEmojis = useMemo(() => {
    if (!hasReactions) return [];
    const recent = new Set();
    for (let i = emojiReactions.length - 1; i >= 0; i--) {
      recent.add(emojiReactions[i].emoji);
      if (recent.size === MAX_VISIBLE_EMOJIS) break;
    }
    return [...recent];
  }, [emojiReactions, hasReactions]);

  // User details are only shown in the modal, so don't fetch them until it opens.
  useEffect(() => {
    if (!isModalOpen || !hasReactions) return;

    let cancelled = false;
    const fetchReactionUsers = async () => {
      const users = {};
      for (const reaction of emojiReactions) {
        if (!users[reaction.userId]) {
          try {
            users[reaction.userId] = await getUserDetailsById(reaction.userId);
          } catch (error) {
            logger.error('Error fetching user details:', error);
          }
        }
      }
      if (!cancelled) setReactionUsers(users);
    };

    fetchReactionUsers();
    return () => {
      cancelled = true;
    };
  }, [isModalOpen, emojiReactions, getUserDetailsById, hasReactions]);

  const handleOpen = useCallback(() => setIsModalOpen(true), []);
  const handleClose = useCallback(() => setIsModalOpen(false), []);

  if (!hasReactions) {
    return null;
  }

  const total = emojiReactions.length;

  return (
    <>
      <div className={styles.emojiArray}>
        <button
          type="button"
          className={`${styles.reactionPill} ${
            topEmojis.length > 1 ? styles.hasMultiple : ''
          }`}
          onClick={handleOpen}
          aria-label={`${total} ${total === 1 ? 'reaction' : 'reactions'}. View who reacted`}
        >
          {topEmojis.map((emoji) => (
            <span key={emoji} className={styles.emoji} aria-hidden="true">
              {emoji}
            </span>
          ))}
        </button>
      </div>
      <ReactionsModal
        isOpen={isModalOpen}
        onClose={handleClose}
        reactions={emojiReactions}
        users={reactionUsers}
      />
    </>
  );
};

export default React.memo(EmojiArray);
